/**
 * The stunt scripts — one function per named manoeuvre in the `RB.EXE`
 * vocabulary. Every one of them returns nothing but stick, rudder and throttle:
 * the AI has no private forces, so a manoeuvre that the airframe cannot fly
 * simply does not come out the way it was asked for, exactly as it would for
 * the player.
 *
 * Scripts are written against `elapsedSeconds` and the aircraft's *own* state
 * (bank, pitch, α), not against a target — the closed-loop, target-tracking
 * scripts live in `pursuit.ts`. Phases inside a stunt key off attitude where
 * attitude is what defines the stunt (an Immelmann rolls upright when it is
 * over the top, not after a fixed count) and off time where the manoeuvre is
 * genuinely open-loop.
 */

import type { FlightInputs } from "../flight/inputs.ts";
import { bankAngle, pitchAngle } from "../flight/math3d.ts";
import { clamp, signOf } from "../flight/units.ts";
import {
  coordinateRudder,
  energyLimitedAlpha,
  inputs,
  limitBank,
  pitchToAlpha,
  pitchToAttitude,
  pullAlphaRad,
  rollToBank,
  speedFraction,
  sustainableBankRad,
} from "./autopilot.ts";
import type { ManeuverContext } from "./maneuvers.ts";

/** −1 or +1; a zero direction defaults right so a script never sits on the fence. */
function turnSign(context: ManeuverContext): number {
  return context.command.direction < 0 ? -1 : 1;
}

/** Bank angle a hard defensive break rolls to, before the energy limiter has its say. */
const BREAK_BANK_RAD = 1.31;
const DIVE_ATTITUDE_RAD = -0.7;
const ZOOM_ATTITUDE_RAD = 0.65;
/**
 * Airspeed fraction of Vmax below which a zoom or a loop gives up and unloads.
 * Set high on purpose: a scout that pulls up until it is hanging on the prop
 * has thrown the fight away, and the AI has to know where that line is.
 */
const MIN_ENERGY_FRACTION = 0.62;

/** DIVE — wings level, nose well down, full throttle: trade height for speed. */
export function diveScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  return inputs({
    pitch: pitchToAttitude(sim, DIVE_ATTITUDE_RAD, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** ZOOM — cash the speed back in for height, unloading before it hangs on the prop. */
export function zoomScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const spent = speedFraction(context.sim) < MIN_ENERGY_FRACTION;
  return inputs({
    pitch: pitchToAttitude(sim, spent ? 0 : ZOOM_ATTITUDE_RAD, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** LOOP — wings level, pull all the way round. Unloads if the energy runs out over the top. */
export function loopScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  if (speedFraction(context.sim) < MIN_ENERGY_FRACTION * 0.7) {
    return inputs({ pitch: 0, roll: rollToBank(sim, 0, tunables), throttle: 1 });
  }
  return inputs({
    pitch: pitchToAlpha(sim, energyLimitedAlpha(sim, tunables), tunables),
    roll: clamp(-sim.rates.roll * tunables.aiRollDamping, -1, 1),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/**
 * HALFLOOP — pull up and over, then stop. Distinguished from IMMEL by *not*
 * rolling upright at the top: it ends inverted, heading back the way it came.
 */
export function halfLoopScript(context: ManeuverContext): FlightInputs {
  const { sim } = context;
  const overTheTop = Math.abs(bankAngle(sim.orientation)) > Math.PI / 2 && pitchAngle(sim.orientation) < 0;
  if (overTheTop) return inputs({ pitch: 0, roll: 0, throttle: 1 });
  return loopScript(context);
}

/** IMMEL — half loop, then roll upright at the top: a reversal that gains height. */
export function immelmannScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const inverted = Math.abs(bankAngle(sim.orientation)) > Math.PI / 2;
  const overTheTop = inverted && pitchAngle(sim.orientation) < 0.15;
  if (!overTheTop) return loopScript(context);
  return inputs({
    pitch: pitchToAttitude(sim, 0, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** SPLIT_S — roll inverted, then pull through: a reversal that trades height for speed. */
export function splitSScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const bank = bankAngle(sim.orientation);
  const rolledOver = Math.abs(bank) > Math.PI * 0.75;
  if (!rolledOver) {
    return inputs({ pitch: 0, roll: turnSign(context), yaw: coordinateRudder(sim, tunables), throttle: 0.6 });
  }
  if (pitchAngle(sim.orientation) > -0.5) {
    return inputs({ pitch: pitchToAlpha(sim, pullAlphaRad(sim, tunables), tunables), throttle: 0.6 });
  }
  return inputs({
    pitch: pitchToAttitude(sim, -0.4, tunables),
    roll: rollToBank(sim, 0, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** A maximum-rate defensive break — the hardest turn the airframe will hold. */
export function breakTurnScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const bank = limitBank(turnSign(context) * BREAK_BANK_RAD, sustainableBankRad(sim));
  return inputs({
    pitch: pitchToAlpha(sim, energyLimitedAlpha(sim, tunables), tunables),
    roll: rollToBank(sim, bank, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/**
 * SLIPTURN — a turn flown deliberately out of balance, rudder against the bank.
 * It is slower and draggier than a clean break, which is the point: it dumps
 * speed and shifts the flight path sideways under a pursuer's gunsight.
 */
export function slipTurnScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const sign = turnSign(context);
  return inputs({
    pitch: pitchToAlpha(sim, energyLimitedAlpha(sim, tunables) * 0.75, tunables),
    roll: rollToBank(sim, limitBank(sign * 0.9, sustainableBankRad(sim)), tunables),
    yaw: -sign * 0.8,
    throttle: 0.8,
  });
}

/** BAR_ROLL — a rolling barrel around the flight path: bleeds closure without losing the nose. */
export function barrelRollScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  return inputs({
    pitch: pitchToAlpha(sim, energyLimitedAlpha(sim, tunables) * 0.5, tunables),
    roll: turnSign(context),
    yaw: coordinateRudder(sim, tunables),
    throttle: 0.9,
  });
}

/** SIDESLIP — rudder one way, aileron the other: the aircraft skids without turning. */
export function sideslipScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const sign = turnSign(context);
  return inputs({
    pitch: pitchToAlpha(sim, sim.readout.alphaRad, tunables),
    roll: rollToBank(sim, -sign * 0.35, tunables),
    yaw: sign,
    throttle: 0.7,
  });
}

/**
 * RETOURN — the reversal: snap the bank to the far side and haul it round the
 * other way. Reads the current bank rather than the command so it always
 * reverses whatever the aircraft was doing.
 */
export function reverseScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  const away = -signOf(bankAngle(sim.orientation)) || turnSign(context);
  const rolled = signOf(bankAngle(sim.orientation)) === away;
  return inputs({
    pitch: rolled ? pitchToAlpha(sim, energyLimitedAlpha(sim, tunables), tunables) : 0,
    roll: rollToBank(sim, limitBank(away * BREAK_BANK_RAD, sustainableBankRad(sim)), tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}
